import { skipToken, useQuery } from '@tanstack/react-query';
import { hydraUiApi } from '@/services/api';
import { useWorkspaceIdFromCurrentUrl } from '@/services/router/use-workspace-id';
import { appKeys, type PushApp } from './entity';

/**
 * The workspace's push apps (mobile app registrations). Only `id` + `name` +
 * `is_default` + `platforms` are read — the delivery detail resolves a push
 * delivery's `app_id` to its app name (Ember `push.loadApps()` +
 * `delivery.appName`), the Design Studio inbox styles section names the
 * default app the visual inbox delivers to, and the people condition
 * builder's device row builds `app_identifier` options from each app's
 * `platforms`. Loaded only behind the `multi_app` flag (or a stored condition
 * that already targets an app), so this stays a thin, opt-in read.
 */

const STALE_TIME = 60_000;

async function getApps(workspaceId: number): Promise<PushApp[]> {
  const data = await hydraUiApi.apps.list({ params: { path: { environment_id: String(workspaceId) } } });
  return (data.apps ?? []).map((app) => ({
    id: app.id,
    name: app.name,
    is_default: app.is_default,
    platforms: app.platforms,
  }));
}

/**
 * The workspace's push apps, fetched only when `enabled` (the `multi_app` flag
 * gates the read) and a workspace id is in scope. Mirrors `useDomainsOptional`:
 * a non-throwing workspace id plus a `skipToken` sentinel key, so a caller that
 * mounts before the workspace settles fetches nothing rather than throwing.
 */
export function useAppsOptional({ enabled = true }: { enabled?: boolean } = {}) {
  const rawWorkspaceId = useWorkspaceIdFromCurrentUrl();
  const workspaceId = rawWorkspaceId != null && rawWorkspaceId > 0 ? rawWorkspaceId : null;
  return useQuery({
    queryKey: enabled && workspaceId != null ? appKeys.list(workspaceId) : appKeys.noWorkspace(),
    queryFn: enabled && workspaceId != null ? () => getApps(workspaceId) : skipToken,
    staleTime: STALE_TIME,
    retry: false,
  });
}
